import { useState, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import MainLayout from '@shared/components/organisms/MainLayout/MainLayout';
import ContentCard from '@shared/components/organisms/ContentCard/ContentCard';
import TabContentLayout from '@shared/components/organisms/TabContentLayout/TabContentLayout';
import ApprovalDataTable from '@shared/components/organisms/ApprovalDataTable/ApprovalDataTable';
import Button from '@shared/components/atoms/Button';
import Pagination from '@shared/components/organisms/Pagination/Pagination';
import SearchFilter from '@shared/components/organisms/SearchFilter/SearchFilter';
import VacationRequestModal from '@pages/vacation/components/VacationRequestModal/VacationRequestModal';
import VacationDetailModal from '@pages/vacation/components/VacationDetailModal';
import { ReactComponent as SaveIcon } from '@shared/assets/images/save-icon.svg';

import { vacationRequestColumns } from '@features/vacation/constants/vacationRequestColumns';
import useVacationRequestPage from '@features/vacation/hooks/useVacationRequestPage';
import useToast from '@shared/hooks/useToast';
import MobileToast from '@shared/components/atoms/MobileToast';

import { useIsMobile } from '@shared/hooks';
import { MobileLayout } from '@shared/components/organisms/mobile';
import MobileVacationRequetContent from '@pages/vacation/components/mobile/MobileVacationRequetContent';
import MobileVacationRequestDetail from '@pages/vacation/components/mobile/MobileVacationRequetContent/MobileVacationRequestDetail';
import MobileVacationRequest from '@pages/vacation/components/mobile/MobileVacationRequetContent/MobileVacationRequest';
import useMobileVacationView from '@features/vacation/hooks/useMobileVacationView';
import Alert from '@shared/components/atoms/Alert';

import styles from './VacationRequestPage.module.scss';

/**
 * VacationRequestPage 컴포넌트
 * - 휴가 신청 목록 페이지 (컨트롤러 역할)
 */
function VacationRequestPage() {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { toastState, showToast, hideToast } = useToast();

  /* ======================
   * 페이지 비즈니스 로직 (훅)
   ====================== */
  const {
    queryYear,
    setQueryYear,
    currentPage,
    setCurrentPage,
    pageSize,
    totalItems,
    tableData,
    vacationStatus,
    isLoading,
    sort,
    refetchAll,
  } = useVacationRequestPage();

  /* ======================
   * UI 전용 상태
   ====================== */
  const currentYear = new Date().getFullYear();

  const [filterValues, setFilterValues] = useState({
    queryYear: queryYear || currentYear.toString(),
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedVacation, setSelectedVacation] = useState(null);

  /* ======================
 * 모바일 Alert 상태
 ====================== */
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const showAlert = useCallback((message) => {
    setAlertMessage(message);
    setAlertOpen(true);
  }, []);

  /* ======================
   * 검색 필터
   ====================== */
  const yearOptions = useMemo(() => {
    const options = [];
    for (let i = currentYear - 5; i <= currentYear + 5; i++) {
      options.push(i.toString());
    }
    return options;
  }, [currentYear]);

  const filterFields = useMemo(() => {
    return [
      {
        key: 'queryYear',
        type: 'select',
        label: '조회년도',
        value: filterValues.queryYear,
        options: yearOptions
          .filter((year) => year <= currentYear)
          .sort((a, b) => b - a) //내림차순
          .map((year) => ({
            value: year,
            label: `${year}년`,
          })),
      },
    ];
  }, [filterValues.queryYear, yearOptions, currentYear]);

  const handleFilterChange = useCallback(
    (key, value) => {
      /**
       * 필터 UI 상태 업데이트
       * SearchFilter는 내부적으로 filterValues를 기준으로 렌더링되므로
       * 선택값이 변경되면 우선 UI 상태를 갱신한다.
       */
      setFilterValues((prev) => ({ ...prev, [key]: value }));
      /**
       * 조회년도(queryYear)가 변경된 경우 즉시 재조회
       * 기존에는 조회 버튼 클릭 시 setQueryYear를 호출했지만,
       * 현재는 select 값이 변경되는 즉시 자동 조회되도록 처리한다.
       */
      if (key === 'queryYear') {
        setQueryYear(value); // 실제 조회에 사용되는 상태 변경 → react-query 자동 재실행
        setCurrentPage(1); // 조회 조건이 바뀌므로 페이지를 1페이지로 초기화
      }
    },
    [setQueryYear, setCurrentPage],
  );

  const handleSearch = useCallback(() => {
    setQueryYear(filterValues.queryYear);
    setCurrentPage(1);
  }, [filterValues.queryYear, setQueryYear, setCurrentPage]);

  /* ======================
   * 이벤트 핸들러
   ====================== */
  function handleRegister() {
    // 관리자가 삭제한 휴가가 목록 캐시에 남아 재신청이 막히지 않도록,
    // 모달을 열 때 본인 휴가 목록을 최신화한다.
    refetchAll();
    setIsModalOpen(true);
  }

  function handleCloseModal() {
    setIsModalOpen(false);
  }

  function handleRowClick(item) {
    setSelectedVacation(item.idx);
    setIsDetailModalOpen(true);
  }

  function handleCloseDetailModal() {
    setIsDetailModalOpen(false);
    setSelectedVacation(null);
  }

  function handleVacationSuccess() {
    refetchAll();
  }

  /* ======================
   * 모바일 뷰 제어
   ====================== */
  const { view, openList, openRequest, openDetail } = useMobileVacationView();

  function handleMobileDetail(item) {
    setSelectedVacation(item.idx);
    openDetail();
  }

  // 현재 년도인지 확인
  const isCurrentYear = filterValues.queryYear === currentYear.toString();

  function renderMobileView() {
    switch (view) {
      case 'REQUEST':
        return (
          <>
            <MobileVacationRequest
              onClose={openList}
              existingVacations={tableData}
              onError={showAlert}
              onSuccess={() => {
                queryClient.invalidateQueries({ queryKey: ['remainVacation'] });
                queryClient.invalidateQueries(['vacationList', filterValues.queryYear]);
                showToast('휴가 신청이 성공했습니다.');
              }}
            />
            <Alert
              isOpen={alertOpen}
              message={alertMessage}
              onClose={() => setAlertOpen(false)}
              className={styles.mobileAlert}
            />
          </>
        );

      case 'DETAIL':
        return (
          <MobileVacationRequestDetail
            vacationIdx={selectedVacation}
            vacationStatus={vacationStatus}
            onClose={openList}
          />
        );

      default:
        return (
          <MobileLayout title="휴가신청">
            <MobileVacationRequetContent
              isLoading={isLoading}
              queryYear={queryYear}
              onChangeYear={setQueryYear}
              vacationStatus={vacationStatus}
              vacationData={tableData}
              onItemClick={handleMobileDetail}
              onClickRequest={() => {
                // 관리자가 삭제한 휴가가 목록 캐시에 남아 재신청이 막히지 않도록,
                // 신청 화면 진입 시 본인 휴가 목록을 최신화한다.
                refetchAll();
                openRequest();
              }}
            />
            <MobileToast
              message={toastState.message}
              isVisible={toastState.isVisible}
              onClose={hideToast}
            />
          </MobileLayout>
        );
    }
  }
  const handleReset = useCallback(() => {
    const year = currentYear.toString();

    // 1. 필터 UI 상태 초기화
    setFilterValues({
      queryYear: year,
    });

    // 2. 실제 조회 조건 초기화
    // setQueryYear(year);

    // 3. 페이지 초기화
    // setCurrentPage(1);
  }, [currentYear, setQueryYear, setCurrentPage]);

  if (isMobile) {
    return renderMobileView();
  }

  /* ======================
   * PC 렌더링
   ====================== */
  return (
    <MainLayout>
      <TabContentLayout
        tabs={{
          label: '휴가 신청',
          content: (
            <ContentCard className={styles.contentCardMarginFix}>
              <div className={styles.vacationRequestPage}>
                {/* 검색 필터 */}
                <div className={styles.filterWrapper}>
                  <SearchFilter
                    fields={filterFields}
                    onFilterChange={handleFilterChange}
                    onSearch={handleSearch}
                    showExcelButton={false}
                    searchButtonText="조회"
                    extraContentPosition="actions"
                    onReset={handleReset}
                    showSearchButton={false}
                    showResetButton={false}
                  />
                </div>

                {/* 휴가 현황 */}
                <div className={styles.statusSection}>
                  <div className={styles.sectionTitle}>휴가 현황</div>
                  <div className={styles.statusTableContainer}>
                    <table className={styles.statusTable}>
                      <thead>
                        <tr>
                          <th colSpan="3">발생분</th>
                          <th colSpan="2">사용분(반영)</th>
                          <th colSpan="2">잔여분</th>
                        </tr>
                        <tr>
                          <th>년초 발생분</th>
                          <th>개정 후 발생분</th>
                          <th>당해 추가분</th>
                          <th>전년 선사용분</th>
                          <th>당해 사용분</th>
                          <th>개정 후</th>
                          <th className={styles.bold}>총 잔여분</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{vacationStatus.generated.beginningOfYear}</td>
                          <td>{vacationStatus.generated.afterRevision}</td>
                          <td>{vacationStatus.generated.currentYearAdditional}</td>
                          <td>{vacationStatus.used.previousYearPreUsed}</td>
                          <td>{vacationStatus.used.currentYearUsed}</td>
                          <td>{vacationStatus.remaining.afterRevision}</td>
                          <td className={styles.bold}>{vacationStatus.remaining.total}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 목록 */}
                <div className={styles.listSection}>
                  <div className={styles.listHeader}>
                    <div className={styles.queryNotice}>
                      <ul>
                        <li>휴가 조회 시 전자결재를 통한 휴가 등록 이력만 조회 가능합니다.</li>
                      </ul>
                    </div>
                    {isCurrentYear && (
                      <Button
                        variant="default"
                        className={styles.registerButton}
                        onClick={handleRegister}
                      >
                        <SaveIcon className={styles.saveIcon} />
                        <span>등록하기</span>
                      </Button>
                    )}
                  </div>

                  <div className={styles.listTableContainer}>
                    {isLoading ? (
                      <div className={styles.loadingContainer}>
                        <div className={styles.circularProgress} />
                        <div className={styles.loadingMessage}>
                          휴가 신청 목록을 불러오는 중입니다...
                        </div>
                      </div>
                    ) : totalItems === 0 ? (
                      <div className={styles.emptyContainer}>현재 휴가 신청 내역이 없습니다.</div>
                    ) : (
                      <ApprovalDataTable
                        columns={vacationRequestColumns}
                        data={tableData}
                        onRowClick={handleRowClick}
                        onSort={sort.handleSort}
                        sortField={{
                          field: sort.sortField,
                          direction: sort.sortDirection,
                        }}
                        getRowKey={(item) => item.idx}
                      />
                    )}
                  </div>

                  {totalItems > 0 && (
                    <div className={styles.paginationWrapper}>
                      <Pagination
                        currentPage={currentPage}
                        totalItems={totalItems}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  )}
                </div>
              </div>
            </ContentCard>
          ),
        }}
      />

      {/* 휴가 신청 모달 */}
      <VacationRequestModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleVacationSuccess}
        existingVacations={tableData}
      />

      {/* 휴가 상세 모달 */}
      <VacationDetailModal
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        vacationIdx={selectedVacation}
        vacationStatus={vacationStatus}
        onAlert={showAlert}
      />

      <Alert isOpen={alertOpen} message={alertMessage} onClose={() => setAlertOpen(false)} />
    </MainLayout>
  );
}

export default VacationRequestPage;
